import React from 'react';
import { MoreHorizontal, Send, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

interface InviteActionsDropdownProps {
  // Pass undefined to hide the corresponding item.
  onResend?: () => void;
  onRevoke?: () => void;
  isResending?: boolean;
  isRevoking?: boolean;
}

/**
 * Reusable row-action dropdown for the Invites table. Mirrors the
 * `<DomainActionsDropdown>` and `<SiteActionsDropdown>` shape so every
 * row-level "..." menu in the admin UI reads the same way.
 *
 * Renders nothing when no action is available (e.g., redeemed / revoked /
 * expired invites). Keeping the cell empty matches the existing row
 * behavior — the column header stays as a column anchor but rows that
 * can't be acted on don't render a stray trigger.
 */
export const InviteActionsDropdown: React.FC<InviteActionsDropdownProps> = ({
  onResend,
  onRevoke,
  isResending = false,
  isRevoking = false,
}) => {
  if (!onResend && !onRevoke) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="tertiary" title="More actions" className="h-8 w-8 hover:no-underline">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {onResend && (
          <DropdownMenuItem onClick={onResend} disabled={isResending}>
            <Send />
            {isResending ? 'Resending…' : 'Resend invite'}
          </DropdownMenuItem>
        )}

        {onRevoke && (
          <>
            {onResend && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={onRevoke}
              disabled={isRevoking}
              className="text-red-400 data-[highlighted]:bg-red-500/15 data-[highlighted]:text-red-300"
            >
              <Trash2 />
              {isRevoking ? 'Revoking…' : 'Revoke invite'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default InviteActionsDropdown;
